import React, { useState, useMemo } from 'react';
import { arrangeSnakeAlgorithm } from '../../../Api/Api.jsx'
import './LayerVisibility.css';

const LayerVisibility = ({ cargos, setCargos, cargoArea, arrangeParameters, onMessage }) => {
  const [expandedLayers, setExpandedLayers] = useState(new Set());

  // Get unique layers and their counts
  const layerStats = useMemo(() => {
    const stats = {};
    cargos.forEach(cargo => {
      const layer = cargo.layer || 1;
      if (!stats[layer]) {
        stats[layer] = {
          total: 0,
          visible: 0,
          active: 0,
          inactive: 0
        };
      }
      stats[layer].total++;
      if (cargo.visible) stats[layer].visible++;
      if (cargo.active) stats[layer].active++;
      if (!cargo.active) stats[layer].inactive++;
    });
    return stats;
  }, [cargos]);

  // Get unique layers sorted
  const layers = useMemo(() => {
    return Object.keys(layerStats)
      .map(Number)
      .sort((a, b) => a - b);
  }, [layerStats]);

  // Toggle layer expansion
  const toggleLayerExpansion = (layer) => {
    const newExpanded = new Set(expandedLayers);
    if (newExpanded.has(layer)) {
      newExpanded.delete(layer);
    } else {
      newExpanded.add(layer);
    }
    setExpandedLayers(newExpanded);
  };

  // Show all items in layer
  const showAllInLayer = (layer) => {
    const updatedCargos = cargos.map(cargo => 
      cargo.layer === layer ? { ...cargo, visible: true } : cargo
    );
    setCargos(updatedCargos);
    onMessage(`All items in Layer ${layer} are now visible`, 'success');
  };

  // Hide all items in layer
  const hideAllInLayer = (layer) => {
    const updatedCargos = cargos.map(cargo => 
      cargo.layer === layer ? { ...cargo, visible: false } : cargo
    );
    setCargos(updatedCargos);
    onMessage(`All items in Layer ${layer} are now hidden`, 'warning');
  };

  // Show only this layer (hide all others)
  const showOnlyThisLayer = (layer) => {
    const updatedCargos = cargos.map(cargo => 
      cargo.layer === layer 
        ? { ...cargo, visible: true }
        : { ...cargo, visible: false }
    );
    setCargos(updatedCargos);
    onMessage(`Showing only Layer ${layer}`, 'info');
  };

  // Show all layers
  const showAllLayers = () => {
    const updatedCargos = cargos.map(cargo => ({ ...cargo, visible: true }));
    setCargos(updatedCargos);
    onMessage('All layers are now visible', 'success');
  };

  // Hide all layers
  const hideAllLayers = () => {
    const updatedCargos = cargos.map(cargo => ({ ...cargo, visible: false }));
    setCargos(updatedCargos);
    onMessage('All layers are now hidden', 'warning');
  };

  // Remove all items in layer (make inactive)
  const removeAllInLayer = (layer) => {
    const activeCargosInLayer = cargos.filter(cargo => 
      cargo.layer === layer && cargo.active
    );
    
    if (activeCargosInLayer.length === 0) {
      onMessage(`No active cargos found in Layer ${layer} to remove`, 'info');
      return;
    }

    const updatedCargos = cargos.map(cargo => 
      cargo.layer === layer && cargo.active 
        ? { 
            ...cargo, 
            active: false,
            x: 0,
            y: 0,
            z: 0
          }
        : cargo
    );
    
    setCargos(updatedCargos);
    onMessage(`Removed ${activeCargosInLayer.length} cargos from Layer ${layer}`, 'warning');
  };

  // Arrange all inactive items in layer
  const arrangeAllInLayer = async (layer) => {
    // Check if cargoArea is defined
    if (!cargoArea || cargoArea.length === 0) {
      onMessage('Please define cargo area first in the side panel', 'error');
      return;
    }

    // Get inactive cargos in this layer
    const inactiveLayerCargos = cargos.filter(cargo => 
      cargo.layer === layer && !cargo.active
    );

    if (inactiveLayerCargos.length === 0) {
      onMessage(`All cargos is arranged yet in Layer ${layer}`, 'info');
      return;
    }

    // Get active cargos (excluding the ones we're about to arrange)
    const activeCargos = cargos.filter(cargo => cargo.active);

    try {
      // Use snake algorithm for arrangement
      const arrangedCuboids = await arrangeSnakeAlgorithm(
        activeCargos,
        inactiveLayerCargos,
        cargoArea,
        arrangeParameters
      );

      if (arrangedCuboids && arrangedCuboids.length > 0) {
        const updatedCargos = cargos.map(cargo => {
          const arrangedCargo = arrangedCuboids.find(cuboid => cuboid['#11_uuid'] === cargo.uuid);
          if (arrangedCargo) {
            return {
              ...cargo,
              x: parseInt(arrangedCargo['#15_position_x']),
              y: parseInt(arrangedCargo['#16_position_y']),
              z: parseInt(arrangedCargo['#17_position_z']),
              active: true,
              visible: true
            };
          }
          return cargo;
        });

        setCargos(updatedCargos);
        onMessage(`Successfully arranged ${inactiveLayerCargos.length} cargos in Layer ${layer}`, 'success');
      } else {
        onMessage(`No cargos were arranged in Layer ${layer}`, 'info');
      }
    } catch (error) {
      onMessage(`Error arranging cargos in Layer ${layer}: ${error.message}`, 'error');
    }
  };

  // Delete all items in layer
  const deleteAllInLayer = (layer) => {
    const cargosInLayer = cargos.filter(cargo => cargo.layer === layer);
    
    if (cargosInLayer.length === 0) {
      onMessage(`No cargos found in Layer ${layer}`, 'info');
      return;
    }

    // Confirm deletion
    if (window.confirm(`Are you sure you want to delete all ${cargosInLayer.length} cargos in Layer ${layer}?`)) {
      const updatedCargos = cargos.filter(cargo => cargo.layer !== layer);
      setCargos(updatedCargos);
      onMessage(`Deleted ${cargosInLayer.length} cargos from Layer ${layer}`, 'warning');
    }
  };

  // Toggle individual cargo visibility
  const toggleCargoVisibility = (uuid) => {
    const updatedCargos = cargos.map(cargo => 
      cargo.uuid === uuid ? { ...cargo, visible: !cargo.visible } : cargo
    );
    setCargos(updatedCargos);
  };

  if (cargos.length === 0) {
    return (
      <div className="layer-visibility-sidebar">
        <h3>Layer Visibility</h3>
        <div className="empty-state">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth="2"/>
          </svg>
          <p>No cargos available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="layer-visibility-sidebar">
      <div className="sidebar-header">
        <h3>Layer Visibility</h3>
        <div className="global-actions">
          <button 
            onClick={showAllLayers}
            className="btn-global btn-show-all"
            title="Show all layers"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2"/>
              <circle cx="12" cy="12" r="3" strokeWidth="2"/>
            </svg>
            Show All
          </button>
          <button 
            onClick={hideAllLayers}
            className="btn-global btn-hide-all"
            title="Hide all layers"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" strokeWidth="2"/>
              <path d="M1 1l22 22" strokeWidth="2"/>
            </svg>
            Hide All
          </button>
        </div>
      </div>

      <div className="layers-list">
        {layers.map(layer => (
          <div key={layer} className="layer-group">
            <div className="layer-header">
              <div 
                className="layer-title"
                onClick={() => toggleLayerExpansion(layer)}
              >
                <span className="layer-name">Layer {layer}</span>
                <div className="layer-stats">
                  <span className={`stat ${layerStats[layer].visible === layerStats[layer].total ? 'all-visible' : ''}`}>
                    👁 {layerStats[layer].visible}/{layerStats[layer].total}
                  </span>
                  <span className="stat-active">
                    ⚡ {layerStats[layer].active}
                  </span>
                  {layerStats[layer].inactive > 0 && (
                    <span className="stat-inactive">
                      ⏸ {layerStats[layer].inactive}
                    </span>
                  )}
                </div>
                <span className={`expand-icon ${expandedLayers.has(layer) ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
              
              <div className="layer-actions">
                <button
                  onClick={() => showOnlyThisLayer(layer)}
                  className="btn-layer btn-show-only"
                  title="Show only this layer"
                >
                  Only
                </button>
                <button
                  onClick={() => showAllInLayer(layer)}
                  className="btn-layer btn-show"
                  title="Show all in layer"
                >
                  Show
                </button>
                <button
                  onClick={() => hideAllInLayer(layer)}
                  className="btn-layer btn-hide"
                  title="Hide all in layer"
                >
                  Hide
                </button>
                
                {/* NEW: Remove All button - only show if there are active cargos */}
                {layerStats[layer].active > 0 && (
                  <button
                    onClick={() => removeAllInLayer(layer)}
                    className="btn-layer btn-remove"
                    title="Remove all active cargos in this layer"
                  >
                    Remove
                  </button>
                )}
                
                {/* Arrange All button - only show if there are inactive cargos */}
                {layerStats[layer].inactive > 0 && (
                  <button
                    onClick={() => arrangeAllInLayer(layer)}
                    className="btn-layer btn-arrange"
                    title="Arrange all other cargos in this layer"
                  >
                    Arrange
                  </button>
                )}
                
                {/* Delete All button */}
                <button
                  onClick={() => deleteAllInLayer(layer)}
                  className="btn-layer btn-delete"
                  title="Delete all cargos in this layer"
                >
                  Delete
                </button>
              </div>
            </div>

            {expandedLayers.has(layer) && (
              <div className="layer-cargos">
                {cargos
                  .filter(cargo => cargo.layer === layer)
                  .map(cargo => (
                    <div key={cargo.uuid} className="cargo-item">
                      <label className="cargo-checkbox">
                        <input
                          type="checkbox"
                          checked={cargo.visible}
                          onChange={() => toggleCargoVisibility(cargo.uuid)}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <span className="cargo-info">
                        <span className="track-number">{cargo.trackNumber}</span>
                        <span className="cargo-dims">
                          {cargo.widthX}×{cargo.heightY}×{cargo.depthZ}
                        </span>
                      </span>
                      <div className="cargo-status">
                        {cargo.active && (
                          <span className="status-badge active" title="Active">A</span>
                        )}
                        {!cargo.active && (
                          <span className="status-badge inactive" title="Inactive">I</span>
                        )}
                        {!cargo.visible && (
                          <span className="status-badge hidden" title="Hidden">H</span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="sidebar-footer">
        <div className="summary">
          Total: {cargos.length} cargos in {layers.length} layers
        </div>
      </div>
    </div>
  );
};

export default LayerVisibility;